import { useState } from 'react'
import { useParams, Link } from 'react-router-dom'

// Mock data - em produção viria de um banco de dados
const areaData = {
  'compliance': {
    name: 'Compliance',
    description: 'Área de conformidade e auditoria',
    processes: [
      { id: '1', name: 'Auditoria Interna Q1', status: 'Em andamento', lastUpdate: '2025-01-20' },
      { id: '2', name: 'Revisão de Políticas', status: 'Concluído', lastUpdate: '2025-01-15' },
      { id: '3', name: 'Treinamento SOX', status: 'Pendente', lastUpdate: '2025-01-10' },
    ]
  },
  'engenharia-dados': {
    name: 'Eng. de Dados',
    description: 'Engenharia e análise de dados',
    processes: [
      { id: '4', name: 'Pipeline ETL Vendas', status: 'Em andamento', lastUpdate: '2025-01-22' },
      { id: '5', name: 'Dashboard Analytics', status: 'Em andamento', lastUpdate: '2025-01-21' },
      { id: '6', name: 'Data Quality Check', status: 'Concluído', lastUpdate: '2025-01-18' },
    ]
  },
  'comercial': {
    name: 'Comercial',
    description: 'Área comercial e vendas',
    processes: [
      { id: '7', name: 'Campanha Q1 2025', status: 'Planejamento', lastUpdate: '2025-01-19' },
      { id: '8', name: 'Análise Funil Vendas', status: 'Em andamento', lastUpdate: '2025-01-20' },
      { id: '9', name: 'Treinamento Equipe', status: 'Concluído', lastUpdate: '2025-01-12' },
    ]
  }
}

const getStatusColor = (status: string) => {
  switch (status) {
    case 'Em andamento': return 'bg-blue-100 text-blue-800'
    case 'Concluído': return 'bg-green-100 text-green-800'
    case 'Pendente': return 'bg-yellow-100 text-yellow-800'
    case 'Planejamento': return 'bg-purple-100 text-purple-800'
    default: return 'bg-gray-100 text-gray-800'
  }
}

export default function AreaPage() {
  const { id } = useParams<{ id: string }>()
  const [searchTerm, setSearchTerm] = useState('')
  
  if (!id || !areaData[id as keyof typeof areaData]) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-gray-900 mb-4">Área não encontrada</h1>
          <Link to="/" className="btn-primary">Voltar ao início</Link>
        </div>
      </div>
    )
  }

  const area = areaData[id as keyof typeof areaData]
  const filteredProcesses = area.processes.filter(process =>
    process.name.toLowerCase().includes(searchTerm.toLowerCase())
  )

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="container mx-auto px-4 py-8">
        {/* Breadcrumb */}
        <nav className="mb-6">
          <Link to="/" className="text-gray-500 hover:text-gray-700">Início</Link>
          <span className="mx-2 text-gray-400">/</span>
          <span className="text-gray-900 font-medium">{area.name}</span>
        </nav>

        {/* Header */}
        <div className="bg-white rounded-xl p-6 shadow-sm mb-8">
          <div className="flex justify-between items-start mb-4">
            <div>
              <h1 className="text-3xl font-bold text-gray-900 mb-2">{area.name}</h1>
              <p className="text-gray-600">{area.description}</p>
            </div>
            <Link 
              to={`/areas/${id}/processo/novo`}
              className="btn-primary"
            >
              + Criar Processo
            </Link>
          </div>
        </div>

        {/* Search and Filters */}
        <div className="bg-white rounded-xl p-6 shadow-sm mb-6">
          <div className="flex flex-col md:flex-row gap-4">
            <div className="flex-1">
              <input
                type="text"
                placeholder="Buscar processos..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
            <select className="px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent">
              <option>Todos os status</option>
              <option>Em andamento</option>
              <option>Concluído</option>
              <option>Pendente</option>
              <option>Planejamento</option>
            </select>
          </div>
        </div>

        {/* Processes List */}
        <div className="space-y-4">
          {filteredProcesses.map((process) => (
            <Link
              key={process.id}
              to={`/areas/${id}/processo/${process.id}`}
              className="block bg-white rounded-xl p-6 shadow-sm hover:shadow-md transition-shadow border border-gray-200 hover:border-gray-300"
            >
              <div className="flex justify-between items-start">
                <div className="flex-1">
                  <h3 className="text-xl font-semibold text-gray-900 mb-2">{process.name}</h3>
                  <div className="flex items-center gap-4 text-sm text-gray-500">
                    <span>Última atualização: {new Date(process.lastUpdate).toLocaleDateString('pt-BR')}</span>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <span className={`px-3 py-1 rounded-full text-xs font-medium ${getStatusColor(process.status)}`}>
                    {process.status}
                  </span>
                  <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                </div>
              </div>
            </Link>
          ))}

          {filteredProcesses.length === 0 && (
            <div className="text-center py-12 bg-white rounded-xl">
              <p className="text-gray-500 mb-4">Nenhum processo encontrado</p>
              <Link 
                to={`/areas/${id}/processo/novo`}
                className="btn-primary"
              >
                Criar primeiro processo
              </Link>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
